import { useState, useMemo, ReactNode } from "react";
import {
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/ui/page-header";
import { SearchInput } from "@/components/ui/search-input";

export interface ColumnDef<T> {
  key: string;
  header: string;
  sortable?: boolean;
  render: (item: T) => ReactNode;
  sortValue?: (item: T) => any;
}

export interface FilterOption {
  label: string;
  placeholder: string;
  options?: string[]; // For future dropdown implementation
}

interface DataTableProps<T> {
  title: string;
  subtitle: string;
  columns: ColumnDef<T>[];
  data: T[];
  loading?: boolean;
  error?: string | null;
  filters?: FilterOption[];
  searchPlaceholder?: string;
  onNextPage?: () => void;
  onPreviousPage?: () => void;
  hasNextPage?: boolean;
  hasPreviousPage?: boolean;
  currentPage?: number;
  itemsPerPage?: number;
  actionButtons?: ReactNode; // Action buttons to display in header
  totalCount?: number; // Total count across all pages
  entityName?: string; // Name of entity for display (e.g., "cases", "URLs")
  selectedId?: string | number | null; // ID of selected row for highlighting
  filterBar?: ReactNode; // Custom filter bar component
}

export function DataTable<T extends { id: string | number }>({
  title,
  subtitle,
  columns,
  data,
  loading = false,
  error = null,
  filters = [],
  searchPlaceholder = "Search...",
  onNextPage,
  onPreviousPage,
  hasNextPage = false,
  hasPreviousPage = false,
  currentPage = 1,
  itemsPerPage,
  actionButtons,
  totalCount,
  entityName = "items",
  selectedId = null,
  filterBar = null,
}: DataTableProps<T>) {
  const [sortField, setSortField] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<"asc" | "desc" | null>(
    null,
  );
  const [searchQuery, setSearchQuery] = useState("");

  const filteredData = useMemo(() => {
    let result = data;

    // Apply search filter (basic - can be enhanced later)
    if (searchQuery) {
      result = result.filter((item) => {
        return columns.some((col) => {
          const value = col.render(item);
          if (typeof value === "string") {
            return value.toLowerCase().includes(searchQuery.toLowerCase());
          }
          return false;
        });
      });
    }

    // Apply sorting
    if (sortField && sortDirection) {
      const column = columns.find((col) => col.key === sortField);
      if (column && column.sortable !== false) {
        result = [...result].sort((a, b) => {
          let aValue: any;
          let bValue: any;

          if (column.sortValue) {
            aValue = column.sortValue(a);
            bValue = column.sortValue(b);
          } else {
            aValue = column.render(a);
            bValue = column.render(b);
          }

          // Handle null/undefined
          if (aValue == null) aValue = "";
          if (bValue == null) bValue = "";

          // Convert to comparable values
          if (typeof aValue === "string") aValue = aValue.toLowerCase();
          if (typeof bValue === "string") bValue = bValue.toLowerCase();

          if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
          if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
          return 0;
        });
      }
    }

    return result;
  }, [data, sortField, sortDirection, searchQuery, columns]);

  const handleSort = (field: string) => {
    if (sortField === field) {
      // Cycle through: asc -> desc -> null
      if (sortDirection === "asc") {
        setSortDirection("desc");
      } else if (sortDirection === "desc") {
        setSortField(null);
        setSortDirection(null);
      }
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const getSortIcon = (field: string) => {
    if (sortField !== field) {
      return <ArrowUpDown className="h-3 w-3 ml-1 text-muted-foreground" />;
    }
    if (sortDirection === "asc") {
      return <ArrowUp className="h-3 w-3 ml-1 text-foreground" />;
    }
    if (sortDirection === "desc") {
      return <ArrowDown className="h-3 w-3 ml-1 text-foreground" />;
    }
    return <ArrowUpDown className="h-3 w-3 ml-1 text-muted-foreground" />;
  };

  // Calculate page display text
  const getPageDisplayText = () => {
    if (!onNextPage && !onPreviousPage) return null;
    
    // Calculate total pages
    let totalPages: number | null = null;
    
    // If we have itemsPerPage, we can calculate total pages
    if (itemsPerPage) {
      // First priority: if we're on the last page (no more pages), we know the total
      if (!hasNextPage) {
        totalPages = currentPage;
      } 
      // Second priority: if we have a valid totalCount, use it
      else if (totalCount !== undefined && totalCount > filteredData.length) {
        totalPages = Math.ceil(totalCount / itemsPerPage);
      }
    }
    
    // If we calculated totalPages, use it
    if (totalPages !== null && totalPages >= currentPage) {
      return `${currentPage} of ${totalPages} pages`;
    }
    
    // If we couldn't calculate and there are more pages, show unknown
    if (hasNextPage) {
      return `${currentPage} of ? pages`;
    }
    
    // On last page but couldn't calculate - this shouldn't happen, but show current page
    return `Page ${currentPage}`;
  };

  const pageDisplayText = getPageDisplayText();

  return (
    <div className="flex flex-col min-h-0 flex-1">
      {/* Header - fixed height */}
      <PageHeader title={title} subtitle={subtitle} className="flex items-start justify-between">
        {actionButtons && (
          <div className="flex items-center gap-2">
            {actionButtons}
          </div>
        )}
      </PageHeader>

      {error && (
        <div className="bg-destructive/10 border border-destructive/20 text-destructive px-4 py-3 rounded mb-4 flex-shrink-0">
          {error}
        </div>
      )}

      {loading ? (
        <div className="text-center py-8">Loading...</div>
      ) : filteredData.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">
          No data found
        </div>
      ) : (
        <div className="flex-1 flex flex-col min-h-0">
          <div className="border rounded-lg overflow-hidden bg-card flex flex-col flex-1 min-h-0">
            {/* Filter section - fixed height */}
            <div className="bg-card flex-shrink-0">
              {/* Search bar */}
              <div className="px-6 py-4">
                <SearchInput
                  placeholder={searchPlaceholder}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>

              {/* Custom filter bar (if provided) */}
              {filterBar}

              {/* Filter dropdowns (legacy placeholders) */}
              {filters.length > 0 && !filterBar && (
                <div className="px-6 pb-4 flex gap-3 flex-wrap">
                  {filters.map((filter, idx) => (
                    <div key={idx} className="flex-1 min-w-[120px]">
                      <div className="h-9 w-full bg-muted rounded-md flex items-center justify-between px-3 cursor-pointer hover:bg-muted/80 transition-colors">
                        <span className="text-xs text-muted-foreground">
                          {filter.placeholder}
                        </span>
                        <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Separator */}
            <div className="border-t border-border flex-shrink-0"></div>

            {/* Showing count and pagination - fixed height */}
            <div className="px-6 py-2 bg-card flex-shrink-0 flex items-center justify-between">
              <div className="text-xs text-muted-foreground">
                {totalCount !== undefined ? (
                  <>
                    Showing {filteredData.length} of {totalCount} {entityName}
                  </>
                ) : (
                  <>
                    Showing {filteredData.length}{" "}
                    {filteredData.length !== 1 ? entityName : entityName.replace(/s$/, "")}
                  </>
                )}
              </div>
              {(onNextPage || onPreviousPage) && (
                <div className="flex items-center gap-2">
                  {pageDisplayText && (
                    <div className="text-sm text-foreground font-medium px-2 min-w-[80px] text-center">
                      {pageDisplayText}
                    </div>
                  )}
                  {onPreviousPage && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={onPreviousPage}
                      disabled={!hasPreviousPage}
                      className="h-7 px-3"
                    >
                      <ChevronLeft className="h-4 w-4 mr-1" />
                      Previous
                    </Button>
                  )}
                  {onNextPage && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={onNextPage}
                      disabled={!hasNextPage || loading}
                      className="h-7 px-3"
                    >
                      Next
                      <ChevronRight className="h-4 w-4 ml-1" />
                    </Button>
                  )}
                </div>
              )}
            </div>

            {/* Separator */}
            <div className="border-t border-border flex-shrink-0"></div>

            {/* Sticky header + scrollable table body */}
            <div className="flex-1 overflow-auto min-h-0 overscroll-contain">
              <table className="w-full">
                <thead className="bg-muted border-b sticky top-0 z-10">
                  <tr>
                    {columns.map((column) => (
                      <th
                        key={column.key}
                        className={`px-4 py-1 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider ${
                          column.sortable !== false
                            ? "cursor-pointer hover:bg-muted/70 transition-colors"
                            : ""
                        }`}
                        onClick={() =>
                          column.sortable !== false && handleSort(column.key)
                        }
                      >
                        <div className="flex items-center">
                          {column.header}
                          {column.sortable !== false && getSortIcon(column.key)}
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="bg-card divide-y divide-border">
                  {filteredData.map((item) => (
                    <tr 
                      key={item.id} 
                      className={`hover:bg-muted/50 transition-colors ${
                        selectedId !== null && item.id === selectedId 
                          ? 'bg-blue-50 hover:bg-blue-100' 
                          : ''
                      }`}
                    >
                      {columns.map((column) => (
                        <td
                          key={column.key}
                          className="px-4 py-3 whitespace-nowrap"
                        >
                          {column.render(item)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

